import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * Cinematic page header used across all corporate sub-pages.
 * Maintains the dark, glassmorphism, neon-green Siemens/Tesla aesthetic.
 */
export const PageHeader = ({ eyebrow, title, accent, lede, breadcrumb }) => {
  const { t } = useTranslation();
  return (
  <section className="relative pt-28 pb-12 lg:pt-36 lg:pb-16 overflow-hidden">
    <div className="absolute inset-0 hx-grid-bg opacity-[0.07] pointer-events-none" />
    <div className="absolute -top-40 -right-60 w-[700px] h-[700px] rounded-full bg-hx-neon/[0.05] blur-[180px] pointer-events-none" />
    <div className="absolute -bottom-40 -left-40 w-[420px] h-[420px] rounded-full bg-emerald-500/[0.03] blur-[140px] pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
      {breadcrumb && (
        <nav className="mb-6 text-[10px] uppercase tracking-[0.22em] text-hx-muted font-mono" data-testid="page-breadcrumb">
          <Link to="/" className="hover:text-hx-neon transition-colors">{t("pages.common.breadcrumb_root")}</Link>
          <span className="mx-2 text-hx-muted/40">/</span>
          <span className="text-hx-text">{breadcrumb}</span>
        </nav>
      )}

      {eyebrow && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-semibold"
        >
          {eyebrow}
        </motion.div>
      )}

      <motion.h1
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="mt-4 font-display font-semibold text-balance text-4xl sm:text-5xl lg:text-[60px] tracking-tight leading-[1.04]"
        data-testid="page-title"
      >
        {title} {accent && <span className="text-hx-neon">{accent}</span>}
      </motion.h1>

      {lede && (
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-6 max-w-3xl text-[15px] lg:text-base text-hx-muted leading-relaxed"
        >
          {lede}
        </motion.p>
      )}
    </div>
  </section>
  );
};
